import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from './api';
import { toast } from 'sonner';
import { ArrowLeft, Mail, Phone, MapPin, Calendar, Banknote, Building2, CreditCard, Download, FileText, UserX, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';

export default function EmployeeDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [employee, setEmployee] = useState(null);
  const [payslips, setPayslips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTerminateDialog, setShowTerminateDialog] = useState(false);
  
  const user = JSON.parse(localStorage.getItem('userData') || localStorage.getItem('hrms_user') || '{}');
  const isManager = user.role === 'department_head';
  const canManage = ['admin', 'hr_manager'].includes(user.role);
  const [terminationData, setTerminationData] = useState({
    termination_date: new Date().toISOString().split('T')[0],
    reason_for_leaving: '',
    termination_type: 'resignation',
    notice_period_served: true,
    last_working_day: new Date().toISOString().split('T')[0],
    hr_feedback: '',
    manager_feedback: '',
    exit_interview_completed: false,
    assets_returned: false,
    final_settlement_amount: 0,
    remarks: ''
  });

  useEffect(() => {
    fetchEmployee();
  }, [id]);

  const fetchEmployee = async () => {
    try {
      // Fetch employee data first
      const empResponse = await axios.get(`${API}/employees/${id}`);
      setEmployee(empResponse.data);
      
      // Fetch payslips separately and handle errors gracefully
      try {
        const payslipResponse = await axios.get(`${API}/payslips`);
        const empPayslips = payslipResponse.data.filter(p => p.employee_id === id);
        setPayslips(empPayslips.sort((a, b) => b.year - a.year || b.month - a.month));
      } catch (payslipError) {
        console.warn('Failed to fetch payslips:', payslipError);
        setPayslips([]); // Set empty array if payslips fail to load
      }
    } catch (error) {
      toast.error('Failed to fetch employee details');
    } finally {
      setLoading(false);
    }
  };

  const downloadForm = async (formType, year) => {
    try {
      toast.info('Generating form...');
      const response = await axios.get(
        `${API}/forms/download/${formType}?employee_id=${id}&year=${year}`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${formType} Form_${employee.full_name}_${year}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Form downloaded successfully!');
    } catch (error) {
      console.error('Error downloading form:', error);
      toast.error('Failed to download form');
    }
  };

  const handleTerminate = async () => {
    if (!terminationData.reason_for_leaving.trim()) {
      toast.error('Please provide a reason for leaving');
      return;
    }

    try {
      await axios.post(`${API}/employees/${id}/terminate`, terminationData);
      toast.success('Employee terminated successfully');
      setShowTerminateDialog(false);
      fetchEmployee(); // Refresh employee data
    } catch (error) {
      console.error('Error terminating employee:', error);
      toast.error('Failed to terminate employee');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">Employee not found</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/suite/hr/employees')}
            data-testid="back-to-employees"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100">Employee Details</h1>
        </div>
        {canManage && (
          <div className="flex items-center space-x-3">
            <Button
              variant="outline"
              onClick={() => navigate(`/suite/hr/employees/${id}/edit`)}
              className="border-slate-300 dark:border-slate-600"
            >
              Edit Employee
            </Button>
            {employee.status === 'active' && (
              <Button
                variant="destructive"
                onClick={() => setShowTerminateDialog(true)}
                className="bg-red-600 hover:bg-red-700"
              >
                <UserX className="h-4 w-4 mr-2" />
                Terminate Employee
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Profile Header */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 p-6 shadow-sm">
        <div className="flex items-start space-x-6">
          <div className="h-24 w-24 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center flex-shrink-0">
            <span className="text-white font-bold text-3xl">
              {employee.full_name.charAt(0)}
            </span>
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100">{employee.full_name}</h2>
              <span className={`status-badge ${
                employee.status === 'active' ? 'status-active' : 'status-inactive'
              }`}>
                {employee.status}
              </span>
            </div>
            <p className="text-slate-600 dark:text-slate-400 text-lg mb-4">{employee.position || 'N/A'}</p>
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center space-x-2 text-slate-600 dark:text-slate-400">
                <Mail className="h-4 w-4" />
                <span>{employee.email}</span>
              </div>
              {employee.phone && (
                <div className="flex items-center space-x-2 text-slate-600 dark:text-slate-400">
                  <Phone className="h-4 w-4" />
                  <span>{employee.phone}</span>
                </div>
              )}
              <div className="flex items-center space-x-2 text-slate-600 dark:text-slate-400">
                <MapPin className="h-4 w-4" />
                <span>{employee.department || 'N/A'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Employment Information */}
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">Employment Information</h3>
          <div className="space-y-3">
            <InfoRow label="Employee ID" value={employee.employee_id} icon={CreditCard} />
            <InfoRow
              label="Join Date"
              value={new Date(employee.join_date).toLocaleDateString('en-MY', { day: '2-digit', month: 'long', year: 'numeric' })}
              icon={Calendar}
            />
            <InfoRow label="Employment Type" value={employee.employment_type.replace('_', ' ').toUpperCase()} icon={Building2} />
            <InfoRow label="IC Number" value={employee.ic_number || 'N/A'} />
          </div>
        </div>

        {/* Salary Information - Hidden from Managers */}
        {!isManager && (
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">Salary Information</h3>
            <div className="space-y-3">
              <InfoRow
                label="Basic Salary"
                value={`RM ${employee.salary_basic.toLocaleString('en-MY', { minimumFractionDigits: 2 })}`}
                icon={Banknote}
              />
              <InfoRow
                label="Allowances"
                value={`RM ${employee.salary_allowances.toLocaleString('en-MY', { minimumFractionDigits: 2 })}`}
              />
              <div className="pt-3 border-t border-slate-100">
                <InfoRow
                  label="Total Salary"
                  value={`RM ${(employee.salary_basic + employee.salary_allowances).toLocaleString('en-MY', { minimumFractionDigits: 2 })}`}
                  bold
                />
              </div>
            </div>
          </div>
        )}

        {/* Statutory Information - Hidden from Managers */}
        {!isManager && (
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">Malaysia Statutory Info</h3>
            <div className="space-y-3">
              <InfoRow label="EPF Number" value={employee.epf_number || 'N/A'} />
              <InfoRow label="SOCSO Number" value={employee.socso_number || 'N/A'} />
              <InfoRow label="EIS Number" value={employee.eis_number || 'N/A'} />
              <InfoRow label="Tax Number" value={employee.tax_number || 'N/A'} />
            </div>
          </div>
        )}

        {/* Bank Information - Hidden from Managers */}
        {!isManager && (
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">Bank Information</h3>
            <div className="space-y-3">
              <InfoRow label="Bank Name" value={employee.bank_name || 'N/A'} />
              <InfoRow label="Bank Account" value={employee.bank_account || 'N/A'} />
            </div>
          </div>
        )}
      </div>

      {/* Payslips Section - Hidden from Managers */}
      {!isManager && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">Recent Payslips</h3>
        {payslips.length > 0 ? (
          <div className="space-y-3">
            {payslips.slice(0, 5).map((payslip, index) => (
              <div key={index} className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-900 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors">
                <div>
                  <p className="font-medium text-slate-900 dark:text-slate-100">
                    {new Date(0, payslip.month - 1).toLocaleString('default', { month: 'long' })} {payslip.year}
                  </p>
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    Net Salary: RM {payslip.net_salary?.toLocaleString('en-MY', { minimumFractionDigits: 2 }) || '0.00'}
                  </p>
                </div>
                <Button variant="outline" size="sm" className="border-slate-300 dark:border-slate-600">
                  <FileText className="h-4 w-4 mr-2" />
                  View
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-slate-500 dark:text-slate-400 text-center py-4">No payslips found</p>
        )}
      </div>
      )}

      {/* Statutory Forms Section */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">Download Statutory Forms ({new Date().getFullYear()})</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Button
            variant="outline"
            onClick={() => downloadForm('EA', new Date().getFullYear())}
            className="flex flex-col items-center justify-center h-24 hover:bg-emerald-50 hover:border-emerald-300"
          >
            <Download className="h-5 w-5 mb-2" />
            <span className="text-sm font-medium">EA Form</span>
            <span className="text-xs text-slate-500">Income Tax</span>
          </Button>
          <Button
            variant="outline"
            onClick={() => downloadForm('FORM_E', new Date().getFullYear())}
            className="flex flex-col items-center justify-center h-24 hover:bg-emerald-50 hover:border-emerald-300"
          >
            <Download className="h-5 w-5 mb-2" />
            <span className="text-sm font-medium">Form E</span>
            <span className="text-xs text-slate-500">EPF Statement</span>
          </Button>
          <Button
            variant="outline"
            onClick={() => downloadForm('PCB2_II', new Date().getFullYear())}
            className="flex flex-col items-center justify-center h-24 hover:bg-emerald-50 hover:border-emerald-300"
          >
            <Download className="h-5 w-5 mb-2" />
            <span className="text-sm font-medium">PCB2(II)</span>
            <span className="text-xs text-slate-500">MTD Statement</span>
          </Button>
          <Button
            variant="outline"
            onClick={() => downloadForm('CP21', new Date().getFullYear())}
            className="flex flex-col items-center justify-center h-24 hover:bg-emerald-50 hover:border-emerald-300"
          >
            <Download className="h-5 w-5 mb-2" />
            <span className="text-sm font-medium">CP21</span>
            <span className="text-xs text-slate-500">Tax Clearance</span>
          </Button>
        </div>
      </div>

      {/* Termination Details (if terminated) */}
      {employee.status === 'terminated' && employee.termination_details && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 shadow-sm">
          <div className="flex items-center space-x-2 mb-4">
            <AlertTriangle className="h-5 w-5 text-red-600" />
            <h3 className="text-lg font-semibold text-red-900">Termination Details</h3>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-3">
              <InfoRow label="Termination Date" value={new Date(employee.termination_details.termination_date).toLocaleDateString('en-MY', { day: '2-digit', month: 'long', year: 'numeric' })} />
              <InfoRow label="Last Working Day" value={new Date(employee.termination_details.last_working_day).toLocaleDateString('en-MY', { day: '2-digit', month: 'long', year: 'numeric' })} />
              <InfoRow label="Termination Type" value={employee.termination_details.termination_type.replace('_', ' ').toUpperCase()} />
              <InfoRow label="Notice Period Served" value={employee.termination_details.notice_period_served ? 'Yes' : 'No'} />
              <InfoRow label="Exit Interview" value={employee.termination_details.exit_interview_completed ? 'Completed' : 'Not Completed'} />
              <InfoRow label="Assets Returned" value={employee.termination_details.assets_returned ? 'Yes' : 'No'} />
              <InfoRow label="Final Settlement" value={`RM ${(employee.termination_details.final_settlement_amount || 0).toLocaleString('en-MY', { minimumFractionDigits: 2 })}`} />
            </div>
            <div className="space-y-4">
              <div>
                <p className="text-sm font-medium text-slate-700 mb-2">Reason for Leaving:</p>
                <p className="text-sm text-slate-600 bg-white p-3 rounded-xl border border-red-100">{employee.termination_details.reason_for_leaving}</p>
              </div>
              {employee.termination_details.hr_feedback && (
                <div>
                  <p className="text-sm font-medium text-slate-700 mb-2">HR Feedback:</p>
                  <p className="text-sm text-slate-600 bg-white p-3 rounded-xl border border-red-100">{employee.termination_details.hr_feedback}</p>
                </div>
              )}
              {employee.termination_details.manager_feedback && (
                <div>
                  <p className="text-sm font-medium text-slate-700 mb-2">Manager Feedback:</p>
                  <p className="text-sm text-slate-600 bg-white p-3 rounded-xl border border-red-100">{employee.termination_details.manager_feedback}</p>
                </div>
              )}
              {employee.termination_details.remarks && (
                <div>
                  <p className="text-sm font-medium text-slate-700 mb-2">Remarks:</p>
                  <p className="text-sm text-slate-600 bg-white p-3 rounded-xl border border-red-100">{employee.termination_details.remarks}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Termination Dialog */}
      <Dialog open={showTerminateDialog} onOpenChange={setShowTerminateDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl flex items-center space-x-2">
              <UserX className="h-6 w-6 text-red-600" />
              <span>Terminate Employee</span>
            </DialogTitle>
            <DialogDescription>
              Please provide all termination details. This action will change the employee status to terminated.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 mt-4">
            {/* Basic Termination Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-700">Termination Date *</Label>
                <Input
                  type="date"
                  value={terminationData.termination_date}
                  onChange={(e) => setTerminationData({ ...terminationData, termination_date: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-slate-700">Last Working Day *</Label>
                <Input
                  type="date"
                  value={terminationData.last_working_day}
                  onChange={(e) => setTerminationData({ ...terminationData, last_working_day: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>

            <div>
              <Label className="text-slate-700">Termination Type *</Label>
              <select
                value={terminationData.termination_type}
                onChange={(e) => setTerminationData({ ...terminationData, termination_type: e.target.value })}
                className="w-full h-10 rounded-md border border-slate-300 px-3 mt-1"
              >
                <option value="resignation">Resignation</option>
                <option value="termination">Termination</option>
                <option value="retirement">Retirement</option>
                <option value="contract_end">Contract End</option>
                <option value="mutual_agreement">Mutual Agreement</option>
                <option value="layoff">Layoff</option>
                <option value="dismissal">Dismissal</option>
              </select>
            </div>

            <div>
              <Label className="text-slate-700">Reason for Leaving *</Label>
              <textarea
                value={terminationData.reason_for_leaving}
                onChange={(e) => setTerminationData({ ...terminationData, reason_for_leaving: e.target.value })}
                rows={3}
                className="w-full rounded-md border border-slate-300 px-3 py-2 mt-1"
                placeholder="Please provide detailed reason for employee leaving..."
              />
            </div>

            <div>
              <Label className="text-slate-700">HR Feedback</Label>
              <textarea
                value={terminationData.hr_feedback}
                onChange={(e) => setTerminationData({ ...terminationData, hr_feedback: e.target.value })}
                rows={3}
                className="w-full rounded-md border border-slate-300 px-3 py-2 mt-1"
                placeholder="HR's feedback on employee performance, conduct, etc..."
              />
            </div>

            <div>
              <Label className="text-slate-700">Manager Feedback</Label>
              <textarea
                value={terminationData.manager_feedback}
                onChange={(e) => setTerminationData({ ...terminationData, manager_feedback: e.target.value })}
                rows={3}
                className="w-full rounded-md border border-slate-300 px-3 py-2 mt-1"
                placeholder="Manager's feedback on employee performance, team collaboration, etc..."
              />
            </div>

            {/* Additional Details */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-700">Final Settlement Amount (RM)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={terminationData.final_settlement_amount}
                  onChange={(e) => setTerminationData({ ...terminationData, final_settlement_amount: parseFloat(e.target.value) || 0 })}
                  placeholder="0.00"
                  className="mt-1"
                />
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="notice_period"
                  checked={terminationData.notice_period_served}
                  onChange={(e) => setTerminationData({ ...terminationData, notice_period_served: e.target.checked })}
                  className="h-4 w-4 rounded border-slate-300"
                />
                <Label htmlFor="notice_period" className="mb-0 cursor-pointer">Notice period served</Label>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="exit_interview"
                  checked={terminationData.exit_interview_completed}
                  onChange={(e) => setTerminationData({ ...terminationData, exit_interview_completed: e.target.checked })}
                  className="h-4 w-4 rounded border-slate-300"
                />
                <Label htmlFor="exit_interview" className="mb-0 cursor-pointer">Exit interview completed</Label>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="assets_returned"
                  checked={terminationData.assets_returned}
                  onChange={(e) => setTerminationData({ ...terminationData, assets_returned: e.target.checked })}
                  className="h-4 w-4 rounded border-slate-300"
                />
                <Label htmlFor="assets_returned" className="mb-0 cursor-pointer">All company assets returned</Label>
              </div>
            </div>

            <div>
              <Label className="text-slate-700">Additional Remarks</Label>
              <textarea
                value={terminationData.remarks}
                onChange={(e) => setTerminationData({ ...terminationData, remarks: e.target.value })}
                rows={2}
                className="w-full rounded-md border border-slate-300 px-3 py-2 mt-1"
                placeholder="Any additional notes or remarks..."
              />
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => setShowTerminateDialog(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleTerminate}
                className="bg-red-600 hover:bg-red-700"
              >
                Confirm Termination
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function InfoRow({ label, value, icon: Icon, bold = false }) {
  return (
    <div className="flex justify-between items-center">
      <div className="flex items-center space-x-2">
        {Icon && <Icon className="h-4 w-4 text-slate-400 dark:text-slate-500" />}
        <span className="text-sm text-slate-600 dark:text-slate-400">{label}</span>
      </div>
      <span className={`text-sm ${bold ? 'font-bold text-slate-900 dark:text-slate-100' : 'text-slate-900 dark:text-slate-100'} tabular-nums`}>
        {value}
      </span>
    </div>
  );
}